import { useState } from "react";
import { useBackNav, goBack } from "../lib/backnav.ts";
import { useQuery } from "@tanstack/react-query";

const TEXT = "#cfe0ff", MUTED = "#8a93a6", GREEN = "#6fcf97", RED = "#e07070",
  AMBER = "#e0a458", ACCENT = "#6699ff", BORDER = "rgba(255,255,255,0.08)",
  CARD = "rgba(255,255,255,0.03)";
const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8,
  padding: "14px 16px", marginBottom: 14 } as const;
const lbl = { fontSize: 11, color: MUTED, letterSpacing: 0.6, marginBottom: 10,
  textTransform: "uppercase" } as const;
const btn = { fontSize: 12, padding: "5px 12px", borderRadius: 5, cursor: "pointer",
  border: "1px solid rgba(255,255,255,0.14)", background: "transparent", color: TEXT } as const;
const backBtn = { ...btn, fontWeight: 700, color: "#cfe0ff",
  border: "1px solid #6699ff88", background: "rgba(102,153,255,0.18)",
  padding: "7px 16px" } as const;
const badge = (ok: boolean) => ({ fontSize: 11, fontWeight: 700, padding: "2px 10px",
  borderRadius: 5, color: ok ? GREEN : RED, background: (ok ? GREEN : RED) + "1a",
  border: `1px solid ${ok ? GREEN : RED}44` });

async function j<T>(p: string): Promise<T> {
  const r = await fetch(p, { headers: { Accept: "application/json" } });
  const b = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error((b as any).detail ?? `Failed (${r.status})`);
  return b as T;
}
const fmt = (v: any, dp = 1) => (v == null || isNaN(Number(v)) ? "—" : Number(v).toFixed(dp));
const usd = (v: any) => (v == null ? "—" : "$" + Math.round(Number(v)).toLocaleString());
const scoringOf = (d: any) => d?.evaluation?.evaluation_output ?? d?.evaluation ?? {};

const DIM_DESC: Record<string, string> = {
  spread_compliance: "How tight the quoted spreads are vs. targets from historical data. Tighter spreads = better pricing.",
  depth_compliance: "USD liquidity on the order book at various price levels. More depth = larger trades without price impact.",
  uptime: "Percentage of time active quotes are maintained. Higher uptime = more reliable liquidity.",
  stress_performance: "Behavior during volatile markets — how much spreads widen and depth decreases.",
  venue_coverage: "How many mandated exchanges are covered. Broader coverage = liquidity everywhere the token trades.",
  transparency: "Data access level — real-time API, audit rights, reporting frequency. Higher = better oversight.",
  economic_alignment: "How compensation structure aligns with foundation goals — retainer, performance fees, loan terms.",
};

type Line = { icon?: "fail" | "warn"; text: string };
function describe(dk: string, det: any): Line[] {
  if (det == null) return [];
  try {
    if (dk === "spread_compliance" && det.per_venue) {
      return Object.entries(det.per_venue).map(([v, x]: [string, any]) =>
        x.note ? { icon: "warn", text: `${v}: ${x.note}` } :
        { icon: x.meets_target ? undefined : "fail",
          text: `${v}: proposed ${fmt(x.proposed_bps)} bps vs target ${fmt(x.target_bps)} bps` });
    }
    if (dk === "depth_compliance" && det.per_venue) {
      return Object.entries(det.per_venue).map(([v, x]: [string, any]) => {
        if (x.note) return { icon: "warn", text: `${v}: ${x.note}` };
        const bands = Object.entries(x.bands ?? {}).map(([b, y]: [string, any]) =>
          y.note ? `±${b}bps: ${y.note}` :
          `±${b}bps: ${usd(y.proposed_usd)} / ${usd(y.target_usd)}` +
          (y.target_usd ? ` (${Math.round(100 * Number(y.proposed_usd) / Number(y.target_usd))}%)` : ""));
        return { text: `${v} (score ${fmt(x.score)}): ${bands.join(", ")}` };
      });
    }
    if (dk === "uptime")
      return [{ text: `committed ${fmt(det.committed)}% vs target ${fmt(det.target)}%` }];
    if (dk === "stress_performance") {
      if (det.note) return [{ text: det.note }];
      if (det.expansion?.target == null && det.retention?.target == null)
        return [{ text: "No stress targets derived for this mandate" }];
      const out: Line[] = [];
      if (det.expansion) out.push({ text:
        `Spread expansion: ${fmt(det.expansion.committed)}× committed vs ${fmt(det.expansion.target)}× target (score: ${fmt(det.expansion.score, 0)})` });
      if (det.retention) out.push({ text:
        `Depth retention: ${fmt(det.retention.committed, 0)}% committed vs ${fmt(det.retention.target, 0)}% target (score: ${fmt(det.retention.score, 0)})` });
      return out;
    }
    if (dk === "venue_coverage") {
      if (det.note) return [{ text: det.note }];
      if (det.mandated == null)
        return [{ text: "No mandated venue requirements" }];
      const out: Line[] = [{ text: `Covers ${det.covered ?? 0} of ${det.mandated} mandated venues` }];
      if (det.missing_venues?.length) out.push({ icon: "warn", text: `Missing: ${det.missing_venues.join(", ")}` });
      return out;
    }
    if (dk === "transparency") {
      const notes = ["api_access", "reporting", "data_format"]
        .map((k) => det[k]?.note).filter(Boolean);
      return notes.length ? [{ text: notes.join("; ") }] : [];
    }
    if (dk === "economic_alignment") {
      const model = String(det.compensation_model ?? "—").replaceAll("_", " ");
      return [{ text: `Structure: ${model.replace(/\b\w/g, (c) => c.toUpperCase())}` }];
    }
  } catch { /* fall through */ }
  if (typeof det === "string") return [{ text: det }];
  if (Array.isArray(det)) return det.map((x) => ({ text: String(x) }));
  return Object.entries(det)
    .filter(([k]) => k !== "comparative_blend" && k !== "per_venue")
    .map(([k, v]) => ({ text: `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}` }));
}

export function EvaluationsTab() {
  const [rfp, setRfp] = useState<string | null>(null);
  const [pid, setPid] = useState<string | null>(null);
  useBackNav([
    { active: rfp != null, close: () => setRfp(null) },
    { active: pid != null, close: () => setPid(null) },
  ]);
  if (rfp && pid) return <Breakdown rfp={rfp} pid={pid} back={goBack} />;
  if (rfp) return <EvalDetail rfp={rfp} back={goBack} openScoring={setPid} />;
  return <EvalList open={setRfp} />;
}

function EvalList({ open }: { open: (r: string) => void }) {
  const rfps = useQuery({ queryKey: ["hitl", "rfps"], queryFn: () => j<any>("/api/v1/hitl/rfps") });
  return (
    <div className="panel">
      <div className="panel-head"><h2 style={{ margin: 0 }}>Evaluations</h2></div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {(rfps.data?.rfps ?? []).map((r: any) => (
          <EvalRow key={r.rfp_id} rfp={r.rfp_id} open={() => open(r.rfp_id)} />
        ))}
      </div>
    </div>
  );
}

function EvalRow({ rfp, open }: { rfp: string; open: () => void }) {
  const sc = useQuery({ queryKey: ["hitl", "scoring", rfp],
    queryFn: () => j<any>(`/api/v1/hitl/rfp/${encodeURIComponent(rfp)}/scoring`) });
  const d = sc.data;
  if (d && !d.scored) return null;
  const ranked: any[] = scoringOf(d).ranked_list ?? [];
  const top = ranked.find((e) => !e.hard_fail);
  return (
    <div style={{ ...card, marginBottom: 0, cursor: "pointer", display: "flex",
      alignItems: "center", gap: 14 }} onClick={open}>
      <div style={{ flex: 1 }}>
        <div style={{ color: ACCENT, fontWeight: 600, fontSize: 14 }}>{rfp}</div>
        <div style={{ color: MUTED, fontSize: 12, marginTop: 4 }}>
          {ranked.length} proposals scored
          {top && <> · Top ranked: <b style={{ color: TEXT }}>{top.entity_name}</b></>}
        </div>
      </div>
      <span style={{ color: MUTED, fontSize: 12 }}>{d?.scored_at ?? ""}</span>
    </div>
  );
}

function EvalDetail({ rfp, back, openScoring }: { rfp: string; back: () => void;
  openScoring: (p: string) => void }) {
  const sc = useQuery({ queryKey: ["hitl", "scoring", rfp],
    queryFn: () => j<any>(`/api/v1/hitl/rfp/${encodeURIComponent(rfp)}/scoring`) });
  const ranked: any[] = scoringOf(sc.data).ranked_list ?? [];
  const th = { textAlign: "left" as const, padding: "8px 12px", fontSize: 10, color: MUTED,
    textTransform: "uppercase" as const, borderBottom: `1px solid ${BORDER}` };
  const td = { padding: "12px", fontSize: 13, color: TEXT, borderBottom: `1px solid ${BORDER}` };
  return (
    <div className="panel">
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button style={backBtn} onClick={back}>← Evaluations</button>
        <h2 style={{ margin: 0 }}>Evaluation Results</h2>
        <span style={{ color: MUTED, fontSize: 12 }}>{ranked.length} proposals scored</span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>{["Rank", "Entity", "Score", "Status"].map((h) => (
          <th key={h} style={th}>{h}</th>))}</tr></thead>
        <tbody>
          {ranked.map((e, i) => (
            <tr key={i}>
              <td style={{ ...td, color: MUTED }}>{e.hard_fail ? "" : `#${e.rank ?? i + 1}`}</td>
              <td style={{ ...td }}>
                <span style={{ color: ACCENT, fontWeight: 700, cursor: "pointer" }}
                  onClick={() => openScoring(e.proposal_id)}>{e.entity_name}</span>
              </td>
              <td style={{ ...td, fontWeight: 700 }}>{fmt(e.final_score)}</td>
              <td style={td}><span style={badge(!e.hard_fail)}>
                {e.hard_fail ? "HARD FAIL" : "PASS"}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Breakdown({ rfp, pid, back }: { rfp: string; pid: string; back: () => void }) {
  const sc = useQuery({ queryKey: ["hitl", "scoring", rfp],
    queryFn: () => j<any>(`/api/v1/hitl/rfp/${encodeURIComponent(rfp)}/scoring`) });
  const b = (scoringOf(sc.data).score_breakdowns ?? []).find((x: any) => x.proposal_id === pid);
  if (!b) return <div style={{ color: MUTED, padding: 20 }}>Loading…</div>;
  return (
    <div className="panel">
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
        <button style={backBtn} onClick={back}>← Results</button>
        <h2 style={{ margin: 0 }}>{b.entity_name} — Scoring Breakdown</h2>
      </div>
      <div style={{ fontSize: 15, color: MUTED, marginBottom: 16 }}>
        Final score: <b style={{ color: ACCENT, fontSize: 24 }}>{fmt(b.final_score)}</b>
        {b.hard_fail && <span style={{ color: RED, fontWeight: 700 }}> · HARD FAIL</span>}
      </div>

      <div style={card}>
        <div style={lbl}>Scoring dimensions</div>
        {Object.entries(b.dimensions ?? {}).map(([dk, dv]: [string, any]) => {
          const aligned = Number(dv?.aligned ?? dv?.aligned_score ?? 0);
          const lines = describe(dk, dv?.details);
          return (
            <div key={dk} style={{ padding: "14px 0", borderBottom: `1px solid ${BORDER}` }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                alignItems: "baseline", flexWrap: "wrap", gap: "2px 12px" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>
                  {dk.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}</span>
                <span style={{ fontSize: 11, color: MUTED }}>
                  RAW <b style={{ color: TEXT }}>{fmt(dv?.raw ?? dv?.raw_score)}</b>
                  {"  "}ALIGNED <b style={{ color: TEXT }}>{fmt(aligned)}</b>
                  {"  "}WEIGHT <b style={{ color: TEXT }}>{fmt(dv?.weight, 2)}</b>
                </span>
              </div>
              {DIM_DESC[dk] && (
                <div style={{ fontSize: 12, color: MUTED, margin: "3px 0 6px" }}>{DIM_DESC[dk]}</div>
              )}
              <div style={{ height: 4, background: "rgba(255,255,255,0.06)",
                borderRadius: 2, margin: "6px 0 8px" }}>
                <div style={{ height: 4, width: `${Math.min(100, aligned)}%`,
                  background: ACCENT, borderRadius: 2 }} />
              </div>
              {lines.length > 0 && (
                <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 6,
                  padding: "10px 14px", fontFamily: "monospace", fontSize: 12, color: TEXT }}>
                  {lines.map((ln, i) => (
                    <div key={i} style={{ padding: "2px 0" }}>
                      {ln.icon === "fail" && <span style={{ color: RED }}>✗ </span>}
                      {ln.icon === "warn" && <span style={{ color: AMBER }}>⚠ </span>}
                      {ln.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={card}>
        <div style={lbl}>Summary</div>
        <div style={{ display: "flex", gap: 40, flexWrap: "wrap" }}>
          {[["Completeness", b.completeness_ratio != null
              ? `${(Number(b.completeness_ratio) * 100).toFixed(0)}%` : "—",
             "of proposal fields filled"],
            ["Multiplier", fmt(b.completeness_multiplier, 2), "applied to weighted score"],
            ["Final score", `${fmt(b.final_score)} / 100.0`,
             "weighted composite × multiplier"]].map(([k, v, s]) => (
            <div key={k}>
              <div style={lbl}>{k}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: ACCENT }}>{v}</div>
              <div style={{ fontSize: 11, color: MUTED }}>{s}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 10, color: MUTED, lineHeight: 1.5 }}>
        DISCLAIMER: This evaluation is generated by the Liquidity Governance Engine and is
        provided for informational purposes only. It does not constitute investment advice,
        fiduciary guidance, or a recommendation to act. The final decision to select, retain,
        or terminate a market maker remains solely with the foundation.
      </div>
    </div>
  );
}
